import { ApiError } from '../../../shared/api.js';

/**
 * Contrôles côté interface avant envoi (le serveur reste seul juge) : nombre, taille, type.
 * Le format HEIC n'est pas refusé ici : l'API répond 415 avec un message clair, affiché tel quel.
 */
export interface PhotoLimits {
  /** Nombre de photos au plus pour cette sélection. */
  max?: number;
  /** Taille au plus d'un fichier (octets). */
  maxBytes?: number;
  /**
   * Types acceptés, au format de l'attribut `accept` (`image/*`, `image/jpeg,image/png`…). Par défaut
   * les images seules : la vidéo n'est pas encore gérée par le serveur (backlog).
   */
  accept?: string;
}

/** Type MIME conforme à une liste `accept` (motifs `type/*`, types exacts ; extensions ignorées). */
export function acceptsType(mime: string, accept = 'image/*'): boolean {
  const type = mime.toLowerCase();
  return accept
    .split(',')
    .map((a) => a.trim().toLowerCase())
    .filter((a) => a && !a.startsWith('.'))
    .some((a) => (a.endsWith('/*') ? type.startsWith(a.slice(0, -1)) : type === a));
}

export interface Rejection {
  name: string;
  message: string;
}

const mb = (bytes: number) => `${Math.round(bytes / 1024 / 1024)} Mo`;

/** Sépare les fichiers acceptés des refusés, dans la limite de `max` (déjà choisis compris). */
export function validatePhotos(picked: File[], alreadyCount: number, limits: PhotoLimits): { accepted: File[]; rejected: Rejection[] } {
  const accepted: File[] = [];
  const rejected: Rejection[] = [];
  for (const file of picked) {
    if (file.type && !acceptsType(file.type, limits.accept)) {
      const imagesOnly = (limits.accept ?? 'image/*').split(',').every((a) => a.trim().toLowerCase().startsWith('image/'));
      rejected.push({ name: file.name, message: imagesOnly ? `« ${file.name} » n’est pas une photo.` : `« ${file.name} » : format non accepté.` });
      continue;
    }
    if (limits.maxBytes !== undefined && file.size > limits.maxBytes) {
      rejected.push({ name: file.name, message: `« ${file.name} » est trop lourde (${mb(limits.maxBytes)} au plus).` });
      continue;
    }
    if (limits.max !== undefined && alreadyCount + accepted.length >= limits.max) {
      rejected.push({ name: file.name, message: `${limits.max} photo${limits.max > 1 ? 's' : ''} au plus : « ${file.name} » n’a pas été ajoutée.` });
      continue;
    }
    accepted.push(file);
  }
  return { accepted, rejected };
}

/** Message d'erreur d'envoi d'une photo : celui de l'API pour un format refusé (HEIC), sinon un texte clair. */
export function uploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 415) return err.message || 'Format non pris en charge : utilisez JPEG, PNG ou WebP.';
    if (err.status === 413) return err.message || 'Photo trop lourde.';
    if (err.status === 429) return 'Trop de photos envoyées pour le moment. Réessayez plus tard.';
    if (err.status === 403) return 'Envoi de photo non autorisé.';
    if (err.status === 501) return 'Envoi de photo pas encore disponible.';
    if (err.status >= 500) return 'Le serveur n’a pas pu enregistrer la photo. Réessayez.';
    return err.message || 'Photo refusée.';
  }
  return 'Envoi impossible. Vérifiez la connexion et réessayez.';
}
